import React, { useState, useEffect } from 'react';
import { InventoryAdminListItem, InventoryScheduleDocument, UpdateInventorySlotRequest } from '../../types';
import { apiService } from '../../services/api';
import { ConfirmDialog } from '../common/ConfirmDialog';

interface InventorySlotManagerProps {
  inventory: InventoryAdminListItem;
  onBack: () => void;
  loading: boolean;
}

export const InventorySlotManager: React.FC<InventorySlotManagerProps> = ({
  inventory,
  onBack,
  loading,
}) => {
  const [slots, setSlots] = useState<InventoryScheduleDocument[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<InventoryScheduleDocument | null>(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditingSlot, setIsEditingSlot] = useState(false);
  const [editFormData, setEditFormData] = useState<UpdateInventorySlotRequest>({});
  const [slotToDelete, setSlotToDelete] = useState<string | null>(null);
  const [priceEditSlot, setPriceEditSlot] = useState<{id: string, price: number} | null>(null);
  const [newPrice, setNewPrice] = useState('');

  // Load slots for this inventory
  const loadSlots = async () => {
    try {
      setIsLoadingSlots(true);
      setError(null);
      const slotsList = await apiService.getSlotsList(inventory.id);
      setSlots(slotsList);
    } catch (err: any) {
      setError(err.message || 'Failed to load slots');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  useEffect(() => {
    loadSlots();
  }, [inventory.id]);

  const handleAddBookings = async (slotId: string) => {
    try {
      setIsLoadingSlots(true);
      await apiService.addBookings(slotId);
      await loadSlots();
    } catch (err: any) {
      setError(err.message || 'Failed to add bookings');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleRemoveBookings = async (slotId: string) => {
    try {
      setIsLoadingSlots(true);
      await apiService.removeBookings(slotId);
      await loadSlots();
    } catch (err: any) {
      setError(err.message || 'Failed to remove bookings');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleDeleteSlot = (slotId: string) => {
    setSlotToDelete(slotId);
  };

  const confirmDeleteSlot = async () => {
    if (!slotToDelete) return;
    const slotId = slotToDelete;
    setSlotToDelete(null);

    try {
      setIsLoadingSlots(true);
      await apiService.deleteSlot(slotId);
      await loadSlots();
    } catch (err: any) {
      setError(err.message || 'Failed to delete slot');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleUpdateSlotStatus = async (slotId: string, status: boolean) => {
    try {
      setIsLoadingSlots(true);
      await apiService.updateSlotStatus(slotId, status);
      await loadSlots();
    } catch (err: any) {
      setError(err.message || 'Failed to update slot status');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleUpdateSlotPrice = async (slotId: string, price: number) => {
    try {
      setIsLoadingSlots(true);
      await apiService.updateSlotPrice(slotId, price);
      await loadSlots();
    } catch (err: any) {
      setError(err.message || 'Failed to update slot price');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleEditSlot = (slot: InventorySlotResponse) => {
    setSelectedSlot(slot);
    setEditFormData({
      notes: slot.notes || '',
      driveLink: slot.driveLink || '',
      info: slot.info || '',
      busInfo: slot.busInfo || '',
      whatsAppLink: slot.whatsAppLink || '',
      busType: slot.busType || '',
      billLink: slot.billLink || '',
    });
    setIsEditingSlot(true);
  };

  const handleSaveSlotEdit = async () => {
    if (!selectedSlot) return;

    try {
      setIsLoadingSlots(true);
      await apiService.updateInventorySlot(selectedSlot.id, editFormData);
      setIsEditingSlot(false);
      setSelectedSlot(null);
      await loadSlots();
    } catch (err: any) {
      setError(err.message || 'Failed to update slot');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const getStatusBadge = (active: boolean) => {
    return active ? (
      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Active</span>
    ) : (
      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Inactive</span>
    );
  };

  const getBookingStatusBadge = (currentCount: number, maxSlots: number) => {
    const percentage = (currentCount / maxSlots) * 100;
    if (percentage >= 100) {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Sold Out</span>;
    } else if (percentage >= 80) {
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Almost Full</span>;
    } else if (percentage >= 30) {
      return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">Filling Fast</span>;
    } else {
      return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">Available</span>;
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-6 pb-0">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-2"
            >
              <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Inventory List
            </button>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Slot Management</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage slots for: <span className="font-medium">{inventory.title}</span>
            </p>
          </div>
          <button
            onClick={loadSlots}
            disabled={isLoadingSlots}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isLoadingSlots ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-6">

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-red-800 dark:text-red-200">{error}</p>
            <div className="flex items-center space-x-3 ml-4">
              <button
                onClick={() => { setError(null); loadSlots(); }}
                className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
              >
                Retry
              </button>
              <button
                onClick={() => setError(null)}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slot Edit Modal */}
      {isEditingSlot && selectedSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Slot: {selectedSlot.displayTime}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Drive Link
                </label>
                <input
                  type="url"
                  value={editFormData.driveLink}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, driveLink: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  WhatsApp Link
                </label>
                <input
                  type="url"
                  value={editFormData.whatsAppLink}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, whatsAppLink: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bus Info
                </label>
                <textarea
                  value={editFormData.busInfo}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, busInfo: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bus Type
                </label>
                <input
                  type="text"
                  value={editFormData.busType}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, busType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsEditingSlot(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSlotEdit}
                disabled={isLoadingSlots}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoadingSlots ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slots List */}
      {isLoadingSlots ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading slots...</span>
        </div>
      ) : slots.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No slots found</h3>
          <p className="text-gray-500 dark:text-gray-400">
            This inventory doesn't have any scheduled slots yet.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {slots.map((slot) => (
            <div
              key={slot.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {slot.displayTime}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {slot.tripTitle}
                  </p>
                  {slot.subText && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {slot.subText}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(slot.active)}
                  {getBookingStatusBadge(slot.currentBookingCount, slot.maxSlots)}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    ₹{slot.price}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Price</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {slot.currentBookingCount}/{slot.maxSlots}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Bookings</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {slot.maxSlots - slot.currentBookingCount}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Available</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {slot.partialPaymentAllowed ? 'Yes' : 'No'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Partial Payment</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleEditSlot(slot)}
                  className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                >
                  Edit
                </button>
                
                <button
                  onClick={() => handleAddBookings(slot.id)}
                  disabled={isLoadingSlots}
                  className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors disabled:opacity-50"
                >
                  +1 Booking
                </button>
                
                <button
                  onClick={() => handleRemoveBookings(slot.id)}
                  disabled={isLoadingSlots}
                  className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors disabled:opacity-50"
                >
                  -1 Booking
                </button>
                
                <button
                  onClick={() => handleUpdateSlotStatus(slot.id, !slot.active)}
                  disabled={isLoadingSlots}
                  className={`px-3 py-1 text-xs rounded transition-colors disabled:opacity-50 ${
                    slot.active
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {slot.active ? 'Deactivate' : 'Activate'}
                </button>
                
                <button
                  onClick={() => {
                    setPriceEditSlot({ id: slot.id, price: slot.price });
                    setNewPrice(slot.price.toString());
                  }}
                  disabled={isLoadingSlots}
                  className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors disabled:opacity-50"
                >
                  Update Price
                </button>
                
                <button
                  onClick={() => handleDeleteSlot(slot.id)}
                  disabled={isLoadingSlots}
                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Price Edit Modal */}
      {priceEditSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4 animate-slide-up">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Update Price</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Price: ₹{priceEditSlot.price}
              </label>
              <input
                type="number"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder="Enter new price"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                autoFocus
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setPriceEditSlot(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (newPrice && !isNaN(parseInt(newPrice))) {
                    handleUpdateSlotPrice(priceEditSlot.id, parseInt(newPrice));
                    setPriceEditSlot(null);
                  }
                }}
                disabled={!newPrice || isNaN(parseInt(newPrice))}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!slotToDelete}
        onConfirm={confirmDeleteSlot}
        onCancel={() => setSlotToDelete(null)}
        title="Delete Slot"
        message="Are you sure you want to delete this slot? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};
